// Effort governor: the only path between a game (race, workout, game, creation) and the trainer's resistance.
// A creation *requests* a gradient, wind, friction or ERG power; the governor bounds, smooths,
// limits the time spent at very high intensity and releases the resistance when you stop pedalling.
import { clamp, CRR } from './physics.js';

export interface EffortRequest {
  /** Simulated gradient (%). */
  grade?: number;
  /** Headwind (m/s, negative = tailwind). */
  wind?: number;
  /** Rolling resistance coefficient (0.004 = road). */
  crr?: number;
  /** Imposed power (W) in ERG mode; null or absent = simulation mode. */
  erg?: number | null;
}
export type TrainerCommand = { mode: 'sim'; grade: number; wind: number; crr: number } | { mode: 'erg'; watts: number };

export interface EffortLimits {
  grade: [number, number]; wind: [number, number]; crr: [number, number];
  /** Maximum ERG, as a multiple of FTP. */
  ergMax: number;
  /** "Very high intensity" threshold (multiple of FTP) and budget of seconds above it, refilled over `window` seconds. */
  hotFrac: number; hotBudget: number; window: number;
  /** ERG cap once the budget is used up (multiple of FTP). */
  ergCapped: number;
  /** Maximum rate of change of the gradient (%/s). */
  gradeSlew: number;
  /** Cadence below which the rider is considered to have stopped, and delay before release (s). */
  stopCadence: number; stopDelay: number;
}

export const DEFAULT_LIMITS: EffortLimits = {
  grade: [-10, 20], wind: [-10, 20], crr: [0.002, 0.015], ergMax: 2, hotFrac: 1.2, hotBudget: 90, window: 300, ergCapped: 1.05,
  gradeSlew: 4, stopCadence: 15, stopDelay: 2,
};
/** Road rolling resistance: the same as the simulation, so the trainer and the screen agree. */
export const ROAD_CRR = CRR;

export class EffortGovernor {
  private grade = 0;
  private hot: number;
  private stopped = 0;
  constructor(public ftp: number, public limits: EffortLimits = DEFAULT_LIMITS) { this.hot = limits.hotBudget; }

  /** Seconds of very high intensity still available. */
  get hotLeft() { return this.hot; }

  /** Command to send to the trainer for this request. `cadence` is used to detect stopping. */
  update(req: EffortRequest, dt: number, cadence: number): TrainerCommand {
    const L = this.limits;
    this.stopped = cadence < L.stopCadence ? this.stopped + dt : 0;
    if (this.stopped >= L.stopDelay) { this.grade = 0; return { mode: 'sim', grade: 0, wind: 0, crr: ROAD_CRR }; }
    if (req.erg != null && Number.isFinite(req.erg)) {
      let w = clamp(req.erg, 0, this.ftp * L.ergMax);
      if (w > this.ftp * L.hotFrac) {
        this.hot = Math.max(0, this.hot - dt);
        if (this.hot <= 0) w = Math.min(w, this.ftp * L.ergCapped);
      } else this.hot = Math.min(L.hotBudget, this.hot + (dt * L.hotBudget) / L.window);
      return { mode: 'erg', watts: Math.round(w) };
    }
    this.hot = Math.min(L.hotBudget, this.hot + (dt * L.hotBudget) / L.window);
    const target = clamp(Number.isFinite(req.grade) ? req.grade! : 0, L.grade[0], L.grade[1]), step = L.gradeSlew * dt;
    this.grade += clamp(target - this.grade, -step, step);
    return {
      mode: 'sim', grade: Math.round(this.grade * 100) / 100,
      wind: clamp(Number.isFinite(req.wind) ? req.wind! : 0, L.wind[0], L.wind[1]),
      crr: clamp(Number.isFinite(req.crr) ? req.crr! : ROAD_CRR, L.crr[0], L.crr[1]),
    };
  }
}

/**
 * Trainer driven without simulation mode (only a resistance level): fraction 0–1 of its range for a gradient.
 * Flat ≈ 15%, a 12% gradient and above = maximum resistance; a descent leaves a minimum to keep some feel.
 */
export function gradeToResistance(grade: number): number {
  return Number.isFinite(grade) ? clamp((grade + 2) / 14, 0, 1) : 0;
}
